import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  collection, query, orderBy, onSnapshot,
  doc, setDoc, deleteDoc, serverTimestamp,
  type DocumentData,
} from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { db } from '@/lib/firebase'
import { useWorkspaceStore } from '@/store/workspace'

export interface Job {
  id: string
  title: string
  slug: string
  department?: string
  location?: string
  type: 'Job' | 'Internship'
  description?: string
  requirements?: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

function docToJob(d: DocumentData & { id: string }): Job {
  const data = d.data ? d.data() : d
  return { id: d.id, ...data } as Job
}

export function useJobs() {
  const { workspaceId } = useWorkspaceStore()
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!workspaceId) return
    setLoading(true)

    const q = query(
      collection(db, `workspaces/${workspaceId}/jobs`),
      orderBy('createdAt', 'desc')
    )

    const unsub = onSnapshot(q, (snap) => {
      setJobs(snap.docs.map(d => docToJob({ id: d.id, ...d.data() })))
      setLoading(false)
    })

    return unsub
  }, [workspaceId])

  return { jobs, loading }
}

export function useSaveJob() {
  const { workspaceId } = useWorkspaceStore()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (job: Omit<Job, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }) => {
      const isNew = !job.id
      const id = job.id || job.slug
      const ref = doc(db, `workspaces/${workspaceId}/jobs/${id}`)
      
      const payload: any = { ...job, updatedAt: serverTimestamp() }
      if (isNew) payload.createdAt = serverTimestamp()

      await setDoc(ref, payload, { merge: true })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["jobs"] })
  })
}

export function useDeleteJob() {
  const { workspaceId } = useWorkspaceStore()
  
  return useMutation({
    mutationFn: async (jobId: string) => {
      await deleteDoc(doc(db, `workspaces/${workspaceId}/jobs/${jobId}`))
    }
  })
}
